const ERROR_CORRECTION_LEVEL_BITS = 1;
const QR_VERSION_CONFIGS = [
  {
    version: 1,
    dataCodewords: 19,
    ecCodewordsPerBlock: 7,
    blockDataLengths: [19],
    alignmentPatternPositions: [],
  },
  {
    version: 2,
    dataCodewords: 34,
    ecCodewordsPerBlock: 10,
    blockDataLengths: [34],
    alignmentPatternPositions: [6, 18],
  },
  {
    version: 3,
    dataCodewords: 55,
    ecCodewordsPerBlock: 15,
    blockDataLengths: [55],
    alignmentPatternPositions: [6, 22],
  },
  {
    version: 4,
    dataCodewords: 80,
    ecCodewordsPerBlock: 20,
    blockDataLengths: [80],
    alignmentPatternPositions: [6, 26],
  },
  {
    version: 5,
    dataCodewords: 108,
    ecCodewordsPerBlock: 26,
    blockDataLengths: [108],
    alignmentPatternPositions: [6, 30],
  },
  {
    version: 6,
    dataCodewords: 136,
    ecCodewordsPerBlock: 18,
    blockDataLengths: [68, 68],
    alignmentPatternPositions: [6, 34],
  },
  {
    version: 7,
    dataCodewords: 156,
    ecCodewordsPerBlock: 20,
    blockDataLengths: [78, 78],
    alignmentPatternPositions: [6, 22, 38],
  },
  {
    version: 8,
    dataCodewords: 194,
    ecCodewordsPerBlock: 24,
    blockDataLengths: [97, 97],
    alignmentPatternPositions: [6, 24, 42],
  },
  {
    version: 9,
    dataCodewords: 232,
    ecCodewordsPerBlock: 30,
    blockDataLengths: [116, 116],
    alignmentPatternPositions: [6, 26, 46],
  },
  {
    version: 10,
    dataCodewords: 274,
    ecCodewordsPerBlock: 18,
    blockDataLengths: [68, 68, 69, 69],
    alignmentPatternPositions: [6, 28, 50],
  },
  {
    version: 11,
    dataCodewords: 324,
    ecCodewordsPerBlock: 20,
    blockDataLengths: [81, 81, 81, 81],
    alignmentPatternPositions: [6, 30, 54],
  },
  {
    version: 12,
    dataCodewords: 370,
    ecCodewordsPerBlock: 24,
    blockDataLengths: [92, 92, 93, 93],
    alignmentPatternPositions: [6, 32, 58],
  },
  {
    version: 13,
    dataCodewords: 428,
    ecCodewordsPerBlock: 26,
    blockDataLengths: [107, 107, 107, 107],
    alignmentPatternPositions: [6, 34, 62],
  },
].map(config => ({
  ...config,
  size: 21 + 4 * (config.version - 1),
}));
const MAX_QR_VERSION_CONFIG = QR_VERSION_CONFIGS[QR_VERSION_CONFIGS.length - 1];

export function createQrSvg(value, scale = 4, border = 4) {
  const config = qrVersionConfigFor(value);
  const matrix = createQrMatrix(value, config);
  const dimension = (config.size + border * 2) * scale;
  const modules = [];

  matrix.forEach((row, y) => {
    row.forEach((isDark, x) => {
      if (isDark) {
        const px = (x + border) * scale;
        const py = (y + border) * scale;
        modules.push(`M${px} ${py}h${scale}v${scale}h-${scale}z`);
      }
    });
  });

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dimension} ${dimension}" width="${dimension}" height="${dimension}" role="img">`,
    '<rect width="100%" height="100%" fill="#fff"/>',
    `<path d="${modules.join('')}" fill="#111"/>`,
    '</svg>',
  ].join('');
}

function createQrMatrix(value, config) {
  const dataCodewords = encodeText(value, config);
  const allCodewords = addErrorCorrection(dataCodewords, config);
  const base = createBaseMatrix(config);
  let bestMatrix = null;
  let bestPenalty = Infinity;

  for (let mask = 0; mask < 8; mask += 1) {
    const matrix = cloneMatrix(base.modules);
    placeCodewords(matrix, base.isFunction, allCodewords, mask, config);
    drawFormatBits(matrix, base.isFunction, mask, config);

    const penalty = calculatePenalty(matrix, config);
    if (penalty < bestPenalty) {
      bestMatrix = matrix;
      bestPenalty = penalty;
    }
  }

  return bestMatrix;
}

function qrVersionConfigFor(value) {
  const bytes = utf8Bytes(value);
  const config = QR_VERSION_CONFIGS.find(versionConfig => (
    textBitLength(bytes.length, versionConfig) <= versionConfig.dataCodewords * 8
  ));

  if (config) {
    return config;
  }

  return MAX_QR_VERSION_CONFIG;
}

function textBitLength(byteLength, config) {
  return 4 + characterCountBits(config) + byteLength * 8;
}

function characterCountBits(config) {
  return config.version < 10 ? 8 : 16;
}

function encodeText(value, config) {
  const bytes = utf8Bytes(value);
  const bits = [];

  appendBits(bits, 0x4, 4);
  appendBits(bits, bytes.length, characterCountBits(config));
  bytes.forEach(byte => appendBits(bits, byte, 8));

  if (bits.length > config.dataCodewords * 8) {
    throw new Error('URL trop longue pour le QR code.');
  }

  appendBits(bits, 0, Math.min(4, config.dataCodewords * 8 - bits.length));

  while (bits.length % 8 !== 0) {
    bits.push(0);
  }

  const data = [];
  for (let i = 0; i < bits.length; i += 8) {
    let codeword = 0;
    for (let j = 0; j < 8; j += 1) {
      codeword = (codeword << 1) | bits[i + j];
    }
    data.push(codeword);
  }

  const padBytes = [0xec, 0x11];
  let padIndex = 0;
  while (data.length < config.dataCodewords) {
    data.push(padBytes[padIndex % 2]);
    padIndex += 1;
  }

  return data;
}

function addErrorCorrection(data, config) {
  const divisor = reedSolomonDivisor(config.ecCodewordsPerBlock);
  const blocks = [];
  let offset = 0;

  config.blockDataLengths.forEach(length => {
    const dataBlock = data.slice(offset, offset + length);
    offset += length;
    blocks.push({
      data: dataBlock,
      errorCorrection: reedSolomonRemainder(dataBlock, divisor),
    });
  });

  const result = [];
  const maxDataLength = Math.max(...config.blockDataLengths);

  for (let i = 0; i < maxDataLength; i += 1) {
    blocks.forEach(block => {
      if (i < block.data.length) {
        result.push(block.data[i]);
      }
    });
  }

  for (let i = 0; i < config.ecCodewordsPerBlock; i += 1) {
    blocks.forEach(block => result.push(block.errorCorrection[i]));
  }

  return result;
}

function createBaseMatrix(config) {
  const modules = Array.from({ length: config.size }, () => Array(config.size).fill(false));
  const isFunction = Array.from({ length: config.size }, () => Array(config.size).fill(false));

  drawFinderPattern(modules, isFunction, 3, 3, config);
  drawFinderPattern(modules, isFunction, config.size - 4, 3, config);
  drawFinderPattern(modules, isFunction, 3, config.size - 4, config);
  drawAlignmentPatterns(modules, isFunction, config);
  drawTimingPatterns(modules, isFunction, config);
  drawFormatBits(modules, isFunction, 0, config);
  drawVersionBits(modules, isFunction, config);
  setFunctionModule(modules, isFunction, 8, config.size - 8, true);

  return { modules, isFunction };
}

function drawFinderPattern(modules, isFunction, centerX, centerY, config) {
  for (let dy = -4; dy <= 4; dy += 1) {
    for (let dx = -4; dx <= 4; dx += 1) {
      const x = centerX + dx;
      const y = centerY + dy;
      if (x < 0 || x >= config.size || y < 0 || y >= config.size) {
        continue;
      }

      const distance = Math.max(Math.abs(dx), Math.abs(dy));
      setFunctionModule(modules, isFunction, x, y, distance !== 2 && distance !== 4);
    }
  }
}

function drawAlignmentPatterns(modules, isFunction, config) {
  config.alignmentPatternPositions.forEach(y => {
    config.alignmentPatternPositions.forEach(x => {
      if (isFunction[y][x]) {
        return;
      }

      for (let dy = -2; dy <= 2; dy += 1) {
        for (let dx = -2; dx <= 2; dx += 1) {
          const distance = Math.max(Math.abs(dx), Math.abs(dy));
          setFunctionModule(
            modules,
            isFunction,
            x + dx,
            y + dy,
            distance === 2 || distance === 0
          );
        }
      }
    });
  });
}

function drawTimingPatterns(modules, isFunction, config) {
  for (let i = 0; i < config.size; i += 1) {
    const isDark = i % 2 === 0;

    if (!isFunction[6][i]) {
      setFunctionModule(modules, isFunction, i, 6, isDark);
    }

    if (!isFunction[i][6]) {
      setFunctionModule(modules, isFunction, 6, i, isDark);
    }
  }
}

function drawFormatBits(modules, isFunction, mask, config) {
  const bits = getFormatBits(mask);

  for (let i = 0; i <= 5; i += 1) {
    setFunctionModule(modules, isFunction, 8, i, getBit(bits, i));
  }
  setFunctionModule(modules, isFunction, 8, 7, getBit(bits, 6));
  setFunctionModule(modules, isFunction, 8, 8, getBit(bits, 7));
  setFunctionModule(modules, isFunction, 7, 8, getBit(bits, 8));
  for (let i = 9; i < 15; i += 1) {
    setFunctionModule(modules, isFunction, 14 - i, 8, getBit(bits, i));
  }

  for (let i = 0; i < 8; i += 1) {
    setFunctionModule(modules, isFunction, config.size - 1 - i, 8, getBit(bits, i));
  }
  for (let i = 8; i < 15; i += 1) {
    setFunctionModule(modules, isFunction, 8, config.size - 15 + i, getBit(bits, i));
  }

  setFunctionModule(modules, isFunction, 8, config.size - 8, true);
}

function drawVersionBits(modules, isFunction, config) {
  if (config.version < 7) {
    return;
  }

  const bits = getVersionBits(config);

  for (let i = 0; i < 18; i += 1) {
    const isDark = getBit(bits, i);
    const a = config.size - 11 + (i % 3);
    const b = Math.floor(i / 3);
    setFunctionModule(modules, isFunction, a, b, isDark);
    setFunctionModule(modules, isFunction, b, a, isDark);
  }
}

function placeCodewords(modules, isFunction, codewords, mask, config) {
  const bits = [];
  codewords.forEach(codeword => appendBits(bits, codeword, 8));

  let bitIndex = 0;
  let upward = true;

  for (let right = config.size - 1; right >= 1; right -= 2) {
    if (right === 6) {
      right -= 1;
    }

    for (let vertical = 0; vertical < config.size; vertical += 1) {
      const y = upward ? config.size - 1 - vertical : vertical;

      for (let j = 0; j < 2; j += 1) {
        const x = right - j;
        if (isFunction[y][x]) {
          continue;
        }

        let isDark = false;
        if (bitIndex < bits.length) {
          isDark = bits[bitIndex] === 1;
          bitIndex += 1;
        }

        if (maskCondition(mask, x, y)) {
          isDark = !isDark;
        }

        modules[y][x] = isDark;
      }
    }

    upward = !upward;
  }
}

function calculatePenalty(matrix, config) {
  let penalty = 0;

  for (let y = 0; y < config.size; y += 1) {
    penalty += linePenalty(matrix[y]);
  }

  for (let x = 0; x < config.size; x += 1) {
    const column = [];
    for (let y = 0; y < config.size; y += 1) {
      column.push(matrix[y][x]);
    }
    penalty += linePenalty(column);
  }

  for (let y = 0; y < config.size - 1; y += 1) {
    for (let x = 0; x < config.size - 1; x += 1) {
      const color = matrix[y][x];
      if (
        matrix[y][x + 1] === color &&
        matrix[y + 1][x] === color &&
        matrix[y + 1][x + 1] === color
      ) {
        penalty += 3;
      }
    }
  }

  penalty += finderPenalty(matrix, config);

  const darkCount = matrix.reduce(
    (total, row) => total + row.filter(Boolean).length,
    0
  );
  const totalCount = config.size * config.size;
  const k = Math.abs(Math.ceil((darkCount * 20) / totalCount) - 10);

  return penalty + k * 10;
}

function linePenalty(line) {
  let penalty = 0;
  let runColor = line[0];
  let runLength = 1;

  for (let i = 1; i < line.length; i += 1) {
    if (line[i] === runColor) {
      runLength += 1;
    } else {
      if (runLength >= 5) {
        penalty += runLength - 2;
      }
      runColor = line[i];
      runLength = 1;
    }
  }

  if (runLength >= 5) {
    penalty += runLength - 2;
  }

  return penalty;
}

function finderPenalty(matrix, config) {
  let penalty = 0;

  for (let y = 0; y < config.size; y += 1) {
    for (let x = 0; x <= config.size - 7; x += 1) {
      if (hasFinderPatternAt(matrix, x, y, 1, 0, config)) {
        penalty += 40;
      }
    }
  }

  for (let x = 0; x < config.size; x += 1) {
    for (let y = 0; y <= config.size - 7; y += 1) {
      if (hasFinderPatternAt(matrix, x, y, 0, 1, config)) {
        penalty += 40;
      }
    }
  }

  return penalty;
}

function hasFinderPatternAt(matrix, x, y, dx, dy, config) {
  const pattern = [true, false, true, true, true, false, true];
  for (let i = 0; i < pattern.length; i += 1) {
    if (matrix[y + dy * i][x + dx * i] !== pattern[i]) {
      return false;
    }
  }

  const beforeIsLight = Array.from({ length: 4 }).every((_, i) => {
    const nx = x - dx * (i + 1);
    const ny = y - dy * (i + 1);
    return nx < 0 || ny < 0 || !matrix[ny][nx];
  });
  const afterIsLight = Array.from({ length: 4 }).every((_, i) => {
    const nx = x + dx * (7 + i);
    const ny = y + dy * (7 + i);
    return nx >= config.size || ny >= config.size || !matrix[ny][nx];
  });

  return beforeIsLight || afterIsLight;
}

function getFormatBits(mask) {
  const data = (ERROR_CORRECTION_LEVEL_BITS << 3) | mask;
  let remainder = data;

  for (let i = 0; i < 10; i += 1) {
    remainder = (remainder << 1) ^ (((remainder >>> 9) & 1) ? 0x537 : 0);
  }

  return ((data << 10) | remainder) ^ 0x5412;
}

function getVersionBits(config) {
  let remainder = config.version;

  for (let i = 0; i < 12; i += 1) {
    remainder = (remainder << 1) ^ (((remainder >>> 11) & 1) ? 0x1f25 : 0);
  }

  return (config.version << 12) | remainder;
}

function reedSolomonDivisor(degree) {
  const result = Array(degree).fill(0);
  result[degree - 1] = 1;
  let root = 1;

  for (let i = 0; i < degree; i += 1) {
    for (let j = 0; j < result.length; j += 1) {
      result[j] = galoisMultiply(result[j], root);
      if (j + 1 < result.length) {
        result[j] ^= result[j + 1];
      }
    }
    root = galoisMultiply(root, 0x02);
  }

  return result;
}

function reedSolomonRemainder(data, divisor) {
  const result = Array(divisor.length).fill(0);

  data.forEach(byte => {
    const factor = byte ^ result.shift();
    result.push(0);
    divisor.forEach((coefficient, index) => {
      result[index] ^= galoisMultiply(coefficient, factor);
    });
  });

  return result;
}

function galoisMultiply(x, y) {
  let z = 0;

  for (let i = 7; i >= 0; i -= 1) {
    z = (z << 1) ^ (((z >>> 7) & 1) ? 0x11d : 0);
    z ^= ((y >>> i) & 1) ? x : 0;
  }

  return z & 0xff;
}

function maskCondition(mask, x, y) {
  switch (mask) {
    case 0:
      return (x + y) % 2 === 0;
    case 1:
      return y % 2 === 0;
    case 2:
      return x % 3 === 0;
    case 3:
      return (x + y) % 3 === 0;
    case 4:
      return (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0;
    case 5:
      return ((x * y) % 2) + ((x * y) % 3) === 0;
    case 6:
      return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
    case 7:
      return (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
    default:
      return false;
  }
}

function setFunctionModule(modules, isFunction, x, y, isDark) {
  modules[y][x] = isDark;
  isFunction[y][x] = true;
}

function appendBits(bits, value, length) {
  for (let i = length - 1; i >= 0; i -= 1) {
    bits.push((value >>> i) & 1);
  }
}

function getBit(value, index) {
  return ((value >>> index) & 1) !== 0;
}

function cloneMatrix(matrix) {
  return matrix.map(row => row.slice());
}

function utf8Bytes(value) {
  if (typeof TextEncoder !== 'undefined') {
    return Array.from(new TextEncoder().encode(value));
  }

  return unescape(encodeURIComponent(value))
    .split('')
    .map(character => character.charCodeAt(0));
}
